import { MessageSquareWarning } from 'lucide-react';
import { useTranslation } from 'react-i18next';

import { useInterviewContext } from '@/features/common/contexts/interview.context';
import {
  INTERVIEW_STEPS,
  useOptionalInterviewFlowContext,
} from '@/features/interview/contexts/interview-flow.context';
import { useBooth } from '@/features/settings/hooks/useBooth';

import { FeedbackSheet } from '../feedback-sheet';
import { useFeedbackContext } from '../feedback.provider';

/** Labels for kiosk flow steps (URL-based routing, pre-interview). */
const KIOSK_STEP_LABELS: Record<string, string> = {
  '/language': 'Choix de la langue',
  '/onboarding': 'Accueil',
  '/register/cgu': 'Inscription — CGU',
  '/register/names': 'Inscription — noms',
  '/register/city': 'Inscription — ville',
  '/register/phone': 'Inscription — téléphone',
  '/otp': 'Code de vérification',
  '/payment': 'Paiement',
  '/payment/proceed': 'Paiement en cours',
};

/** Labels for interview flow steps (context-based routing, during interview). */
const INTERVIEW_STEP_LABELS: Record<INTERVIEW_STEPS, string> = {
  [INTERVIEW_STEPS.INTRO]: 'Introduction',
  [INTERVIEW_STEPS.INTERVIEW]: 'Entretien vocal',
  [INTERVIEW_STEPS.PHOTO]: 'Photo',
  [INTERVIEW_STEPS.SUMMARY]: 'Résumé',
};

export function FeedbackFab() {
  const { t } = useTranslation();
  const { isOpen, open, close, playAssistant } = useFeedbackContext();
  const { interview } = useInterviewContext();
  const flowContext = useOptionalInterviewFlowContext();
  const { booth } = useBooth();

  const handleOpen = () => {
    open();
    playAssistant('categories_intro');
  };

  const handleClose = () => {
    close();
  };

  const flowStep = (() => {
    if (flowContext) {
      // During interview: use summarySubStep when in SUMMARY, else step label
      if (flowContext.step === INTERVIEW_STEPS.SUMMARY && flowContext.summarySubStep) {
        return flowContext.summarySubStep;
      }
      return INTERVIEW_STEP_LABELS[flowContext.step] ?? 'Interview';
    }
    // Kiosk flow (pre-interview): map pathname to French label
    const path = window.location.pathname.replace(/\/$/, '') || '/';
    return KIOSK_STEP_LABELS[path] ?? path;
  })();

  return (
    <>
      <div className="fixed bottom-[56px] right-4 md:bottom-4 z-[60]">
        <button
          type="button"
          onClick={handleOpen}
          aria-label={t('Feedback.fabLabel')}
          className="flex h-14 items-center gap-2 rounded-full bg-[#f9594c] px-5 py-3 text-white shadow-lg transition-transform hover:scale-105 active:scale-95"
        >
          <MessageSquareWarning className="h-5 w-5" />
          <span className="text-sm font-medium">{t('Feedback.fabLabel')}</span>
        </button>
      </div>

      <FeedbackSheet
        open={isOpen}
        onOpenChange={handleClose}
        boothId={booth?.id}
        sessionId={interview?.id}
        flowStep={flowStep}
        lastMessage={flowContext?.lastMessage}
      />
    </>
  );
}
